import { useState, useEffect, useCallback, useRef } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  Card, Button, Modal, Input, EmptyState, Badge, SearchBar, Pagination,
  FilterBar, PageHeader, ListSkeleton, IconButton, Avatar, Chip, StickyActions, cx,
  ListPage, ListSurface, stickyHeadCls, actionColCls, actionHeadCls,
  listTableCls, listCardsCls, thCls, tdCls, actionW,
} from "../components/UI";
import api from "../utils/api";
import toast from "react-hot-toast";
import { Plus, Pencil, Trash2, Mail, Phone, HardHat, UserCheck, UserX, MapPin } from "lucide-react";

const LIMIT = 15;
const INIT = { name: "", email: "", password: "", phone: "", role: "", employeeId: "" };

function SiteChips({ sites = [], max = 2 }) {
  if (!sites.length) return <span className="text-xs text-ink-300">Unassigned</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {sites.slice(0, max).map(s => <Chip key={s._id || s} tone="brand">{s.name || s}</Chip>)}
      {sites.length > max && <Chip tone="slate">+{sites.length - max}</Chip>}
    </div>
  );
}

function CrewCard({ c, onEdit, onDelete, onToggle }) {
  return (
    <Card className="p-4" hover>
      {/* actions lead the card, mirroring the table's first column */}
      <div className="flex items-center gap-1.5 pb-3 mb-3 border-b border-ink-100">
        <Button size="xs" variant="outline" icon={<Pencil size={13} />} onClick={() => onEdit(c)}>Edit</Button>
        <Button size="xs" variant={c.isActive ? "secondary" : "soft"} onClick={() => onToggle(c)}
          icon={c.isActive ? <UserX size={13} /> : <UserCheck size={13} />}>
          {c.isActive ? "Disable" : "Enable"}
        </Button>
        <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => onDelete(c._id)} title="Delete" className="ml-auto" />
      </div>

      <div className="flex items-start gap-3">
        <Avatar name={c.name} tone="emerald" size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-ink-900 truncate">{c.name}</span>
            <Badge variant={c.isActive ? "success" : "default"} size="xs" dot>{c.isActive ? "Active" : "Inactive"}</Badge>
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {c.employeeId && <span className="text-[11px] font-mono text-ink-400">#{c.employeeId}</span>}
            {c.role && <span className="text-xs text-ink-500">{c.role}</span>}
          </div>
        </div>
      </div>

      <div className="mt-3 space-y-1.5 text-xs">
        <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-ink-600 hover:text-brand-600">
          <Mail size={13} className="text-ink-400 shrink-0" /><span className="truncate">{c.email}</span>
        </a>
        {c.phone && (
          <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-ink-600 hover:text-brand-600">
            <Phone size={13} className="text-ink-400 shrink-0" />{c.phone}
          </a>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-ink-100 min-w-0">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-ink-400 uppercase tracking-wider mb-1.5">
          <MapPin size={11} /> Sites
        </div>
        <SiteChips sites={c.assignedSites} />
      </div>
    </Card>
  );
}

export default function AdminCrews() {
  const [crews, setCrews] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INIT);
  const [saving, setSaving] = useState(false);
  const debRef = useRef(null);

  const fetchCrews = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: LIMIT });
      if (search) p.set("search", search);
      const { data } = await api.get(`/crews?${p}`);
      setCrews(data.data.crews || []);
      setTotal(data.data.total);
      setPages(data.data.pages);
    } catch { toast.error("Failed to load crews"); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchCrews(); }, [fetchCrews]);

  const handleSearch = (v) => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 350);
    setSearch(v);
  };

  const openCreate = () => { setForm(INIT); setSelected(null); setModal("create"); };
  const openEdit = (c) => {
    setSelected(c);
    setForm({ name: c.name, email: c.email, password: "", phone: c.phone || "", role: c.role || "", employeeId: c.employeeId || "" });
    setModal("edit");
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form };
      if (modal === "edit" && !payload.password) delete payload.password;
      if (modal === "create") { await api.post("/crews", payload); toast.success("Crew member added!"); }
      else { await api.put(`/crews/${selected._id}`, payload); toast.success("Crew updated!"); }
      setModal(null); fetchCrews();
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this crew member?")) return;
    try { await api.delete(`/crews/${id}`); toast.success("Crew deleted"); fetchCrews(); }
    catch { toast.error("Failed"); }
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/crews/${c._id}`, { isActive: !c.isActive });
      toast.success(c.isActive ? "Deactivated" : "Activated");
      fetchCrews();
    } catch { toast.error("Failed"); }
  };

  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <AdminLayout fullHeight>
      <ListPage>
        <PageHeader
          title="Crew Members"
          subtitle={`${total} member${total !== 1 ? "s" : ""} in the field`}
          action={<Button onClick={openCreate} icon={<Plus size={16} />} className="w-full sm:w-auto">Add Crew</Button>}
        />

        <FilterBar className="shrink-0">
          <SearchBar value={search} onChange={handleSearch} placeholder="Search name, email, role, ID…" className="flex-1 min-w-[180px]" />
          {search && (
            <button onClick={() => { setSearch(""); setPage(1); }} className="text-xs font-semibold text-brand-600 hover:text-brand-700 px-1">Clear</button>
          )}
        </FilterBar>

        {loading ? (
          <div className="flex-1 min-h-0 overflow-hidden"><ListSkeleton rows={6} cols={6} /></div>
        ) : crews.length === 0 ? (
          <Card className="flex-1 min-h-0 grid place-items-center">
            <EmptyState icon={<HardHat size={22} />} title="No crew members found"
              description={search ? "Try a different search term." : "Add your first crew member to assign them to sites."}
              action={!search && <Button onClick={openCreate} icon={<Plus size={16} />}>Add Crew</Button>} />
          </Card>
        ) : (
          <ListSurface footer={<Pagination page={page} pages={pages} total={total} limit={LIMIT} onPage={setPage} />}>
            {/* ── Desktop table ── */}
            <table className={listTableCls}>
              <thead className={stickyHeadCls}>
                <tr>
                  <th className={cx(thCls, actionHeadCls, actionW(3))}>Actions</th>
                  <th className={thCls}>Member</th>
                  <th className={thCls}>Contact</th>
                  <th className={cx(thCls, "w-[140px]")}>Role</th>
                  <th className={thCls}>Sites</th>
                  <th className={cx(thCls, "w-[120px]")}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {crews.map(c => (
                  <tr key={c._id} className="group hover:bg-ink-50 transition-colors">
                    <td className={cx(tdCls, actionColCls, "px-3")}>
                      <div className="flex gap-0.5">
                        <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} onClick={() => openEdit(c)} title="Edit" />
                        <IconButton size="sm" variant="outline" icon={c.isActive ? <UserX size={15} /> : <UserCheck size={15} />}
                          onClick={() => toggleActive(c)} title={c.isActive ? "Deactivate" : "Activate"}
                          className={cx(c.isActive ? "hover:text-amber-600 hover:bg-amber-50" : "hover:text-emerald-600 hover:bg-emerald-50")} />
                        <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(c._id)} title="Delete" />
                      </div>
                    </td>
                    <td className={tdCls}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar name={c.name} tone="emerald" size="sm" />
                        <div className="min-w-0">
                          <div className="font-semibold text-ink-800 truncate">{c.name}</div>
                          {c.employeeId && <div className="text-[11px] text-ink-400 font-mono truncate">#{c.employeeId}</div>}
                        </div>
                      </div>
                    </td>
                    <td className={tdCls}>
                      <a href={`mailto:${c.email}`} className="block text-xs text-ink-600 hover:text-brand-600 truncate">{c.email}</a>
                      {c.phone && <a href={`tel:${c.phone}`} className="block text-xs text-ink-400 hover:text-brand-600 truncate">{c.phone}</a>}
                    </td>
                    <td className={tdCls}>
                      {c.role
                        ? <Badge variant="info" className="max-w-full"><span className="truncate">{c.role}</span></Badge>
                        : <span className="text-ink-300 text-xs">—</span>}
                    </td>
                    <td className={tdCls}><SiteChips sites={c.assignedSites} /></td>
                    <td className={tdCls}>
                      <Badge variant={c.isActive ? "success" : "default"} dot>{c.isActive ? "Active" : "Inactive"}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── Mobile / tablet cards ── */}
            <div className={listCardsCls}>
              {crews.map(c => <CrewCard key={c._id} c={c} onEdit={openEdit} onDelete={handleDelete} onToggle={toggleActive} />)}
            </div>
          </ListSurface>
        )}
      </ListPage>

      <Modal
        open={modal === "create" || modal === "edit"}
        onClose={() => setModal(null)}
        title={modal === "create" ? "Add Crew Member" : "Edit Crew Member"}
        subtitle={modal === "edit" ? selected?.name : "They'll sign in to the crew app with these details"}
        icon={<HardHat size={18} />}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Full Name" required {...f("name")} placeholder="John Doe" />
            <Input label="Employee ID" {...f("employeeId")} placeholder="EMP001" className="font-mono" />
          </div>
          <Input label="Email" type="email" required {...f("email")} placeholder="crew@hilandsurveyors.in" icon={<Mail size={15} />} />
          <Input
            label={modal === "edit" ? "New Password" : "Password"}
            hint={modal === "edit" ? "Leave blank to keep the current password" : "Minimum 6 characters"}
            type="password" required={modal === "create"} minLength={modal === "create" ? 6 : undefined}
            {...f("password")} placeholder="••••••••"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Phone" type="tel" {...f("phone")} placeholder="+91 98765 43210" icon={<Phone size={15} />} />
            <Input label="Role / Designation" {...f("role")} placeholder="Surveyor, Geologist…" />
          </div>

          <StickyActions>
            <Button type="button" variant="secondary" onClick={() => setModal(null)} block className="sm:w-auto">Cancel</Button>
            <Button type="submit" loading={saving} block className="sm:w-auto">
              {modal === "create" ? "Add Crew" : "Save Changes"}
            </Button>
          </StickyActions>
        </form>
      </Modal>
    </AdminLayout>
  );
}
